import { trait } from '@traits-ts/core'
import type { TaskActionButton, TaskListAction, TaskPriority, TaskType } from '../../types/task.js'

/**
 * WithUIConfiguration trait
 * Adds UI representation capabilities to tasks
 */
export const WithUIConfiguration = trait(superclass => class extends superclass {
  /**
   * Get the UI configuration for this task
   * @returns Configuration object for task UI rendering
   */
  getUIConfig() {
    return {
      icon: this.getTaskIcon(),
      color: this.getTaskColor(),
      actionButtons: this.getActionButtons(),
      listActions: this.getListActions(),
    }
  }

  /**
   * Get the icon to use for this task
   * @returns Icon class name
   */
  getTaskIcon(): string {
    const icons: Record<TaskType, string> = {
      member: 'pi pi-user',
      subkrewe: 'pi pi-users',
      admin: 'pi pi-cog',
      system: 'pi pi-server',
    }
    return icons[this.taskType] || 'pi pi-check-circle'
  }

  /**
   * Get the color to use for this task
   * @returns CSS color value
   */
  getTaskColor(): string {
    const colors: Record<TaskPriority, string> = {
      low: 'var(--blue-500)',
      medium: 'var(--primary-color)',
      high: 'var(--orange-500)',
      critical: 'var(--red-500)',
    }
    return colors[this.priority] || 'var(--primary-color)'
  }

  /**
   * Get actions for detailed task view
   * @returns Array of action buttons
   */
  getActionButtons(): TaskActionButton[] {
    const buttons: TaskActionButton[] = []

    if (this.status === 'pending') {
      buttons.push({
        label: 'Start',
        icon: 'pi pi-play',
        action: 'start',
      })
    }

    return buttons
  }

  /**
   * Get actions for task list view
   * @returns Array of list actions
   */
  getListActions(): TaskListAction[] {
    return [] // Default is no row-level actions
  }

  /**
   * Get custom component for detailed task view
   * @returns Component or null if no custom component
   */
  getTaskComponent(): unknown {
    return null // Default is no custom component
  }
})

export interface WithUIConfiguration {
  // Existing methods
  getTaskIcon(): string
  getTaskColor(): string
  getActionButtons(): TaskActionButton[]
  getListActions(): TaskListAction[]
  getDetailsComponent?: () => never

  // New method for metadata display
  getMetadataDisplayFields(): MetadataField[]
}

// Define a metadata field type
export interface MetadataField {
  key: string // Metadata key
  label: string // Display label
  icon?: string // Optional icon (PrimeVue icon class)
  type?: 'text' | 'date' | 'boolean' | 'status' // Data type
  condition?: (task: never) => boolean // Optional condition to show field
}
